import { IEquipment, IEquipmentPassport } from '@/pages/Equipment/equipment.types.ts'
import equipmentUtils from '@/pages/Equipment/equipment.utils.ts'
import SeparateLine from '@/shared/Forms/SeparateLine'
import Button from '@/shared/Buttons/Button'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import equipmentApi from '@/pages/Equipment/equipment.api.ts'
import equipmentEvents from '@/pages/Equipment/events/equipment/equipment.events.ts'
import { useMemo, useState } from 'react'
import EquipmentPassportEditingStep from './EquipmentPassportEditingStep'
import equipmentSidePageEditContentConstants from './equipmentSidePage.constants'
import StepIndicator from '@/shared/Common/StepIndicator'
import usePassportDifferences from './hooks/usePassportDifferences'
import useEquipmentForm from '../EquipmentFormFields/hooks/useEquipmentForm'
import usePassportSchema from '@/pages/MDM/hooks/usePassprotSchema'
import EquipmentPassportViewingStep from './EquipmentPassportViewingStep'
import SidePage from '@/shared/SidePage'

interface IProps {
  passport: IEquipmentPassport
  equipment: IEquipment
  onCancel: () => void
  open: boolean
}

const EditEquipmentPassportSidePage = (props: IProps) => {
  const passportSchema = usePassportSchema(props.passport?.passportId)
  const [stepIndex, setStepIndex] = useState<number>(0)
  const { wait, loading } = useHttpLoader()

  const preparedPassportDTO = useMemo(() => {
    const passportDto = equipmentUtils.passportInstanceToPassportDTO(props.passport)
    return equipmentUtils.fillMissingPassportValues(passportDto, passportSchema)
  }, [props.passport, passportSchema])

  const ctrl = useEquipmentForm({
    passport: preparedPassportDTO || { passportSchemaId: null, values: [] },
    equipment: null,
  })

  const { passportDifferences, changedPassportSchemaFieldIds } = usePassportDifferences({
    prevPassportValues: props.passport.values,
    passportValues: ctrl.passport.values,
  })

  const handleSave = () => {
    wait(equipmentApi.putEquipmentPassport(props.equipment.id, ctrl.passport), (resp) => {
      if (resp.status === 'success') {
        equipmentEvents.updated({ ...props.equipment, passportInstanceId: resp.body.id })
        props.onCancel()
      }
    })
  }

  const renderFooterButtons = () => {
    if (stepIndex === 0) {
      return (
        <>
          <Button
            size="default"
            type="primary"
            text="Далее"
            disabled={changedPassportSchemaFieldIds.length === 0}
            onClick={() => setStepIndex(1)}
          />
          <Button size="default" type="default" text="Отменить" onClick={props.onCancel} />
        </>
      )
    }

    return (
      <>
        <Button size="default" type="primary" text="Сохранить" loading={loading} onClick={handleSave} />
        <Button size="default" type="default" text="Назад" onClick={() => setStepIndex(0)} />
      </>
    )
  }

  const renderContent = () => {
    if (stepIndex === 0) return <EquipmentPassportEditingStep ctrl={ctrl} />
    return (
      <EquipmentPassportViewingStep
        passportDifferences={passportDifferences}
        changedPassportSchemaFieldIds={changedPassportSchemaFieldIds}
        passportId={props.passport.passportId}
      />
    )
  }

  return (
    <SidePage onClose={props.onCancel} open={props.open} size="large" style={{ paddingRight: 0, paddingBottom: 0 }}>
      <div className="flex-lines height100 gap16" style={{ scrollBehavior: 'smooth' }}>
        <div className="flex-lines gap16" style={{ paddingRight: '24px' }}>
          <h4 className="text-heading-4">{equipmentSidePageEditContentConstants.stpesNames[stepIndex]}</h4>
          <StepIndicator stepNames={equipmentSidePageEditContentConstants.stpesNames} activeStepIndex={stepIndex} />
          <SeparateLine />
        </div>
        {renderContent()}
        <div style={{ marginTop: 'auto', paddingRight: '24px' }}>
          <SeparateLine />
        </div>
        <div className="inline-flex-gap" style={{ paddingBottom: '20px' }}>
          {renderFooterButtons()}
        </div>
      </div>
    </SidePage>
  )
}

export default EditEquipmentPassportSidePage
